import React, { useEffect, useState, useMemo } from 'react';
import { MultiSelectCheckboxTag } from 'lift-ui';
import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, SpinnerLoader } from 'lift-ui';
import { HelpCircle } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import apiClient from '@lift-admin/utils/apiClient';

interface OrganizationMultiSelectProps {
  defaultOrganizations: number[];
  setDefaultOrganizations: any
}

// Helper function to parse and normalize organization IDs
const normalizeOrganizationId = (id: any): number => {
  if (id === undefined || id === null) return 0;
  return typeof id === 'number' ? id : parseInt(id, 10);
};

const OrganizationMultiSelect = ({ defaultOrganizations, setDefaultOrganizations }: OrganizationMultiSelectProps) => {
  console.log('Current defaultOrganizations:', defaultOrganizations);
  
  const [allOrganizations, setAllOrganizations] = useState<any[]>([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Determine the initial mode based on the defaultOrganizations
  const initialMode = useMemo(() => {
    if (!defaultOrganizations || !Array.isArray(defaultOrganizations) || defaultOrganizations.length === 0) return 'exclude';
    
    // If any positive ID exists, we're in include mode
    return defaultOrganizations.some(id => normalizeOrganizationId(id) > 0) ? 'include' : 'exclude';
  }, []);
  
  const [mode, setMode] = useState<'exclude' | 'include'>(initialMode);
  
  // Determine if we're in mixed mode (has both positive and negative IDs)
  const hasMixedMode = useMemo(() => {
    if (!defaultOrganizations || !Array.isArray(defaultOrganizations)) return false;
    
    const hasPositive = defaultOrganizations.some(id => normalizeOrganizationId(id) > 0);
    const hasNegative = defaultOrganizations.some(id => normalizeOrganizationId(id) < 0);
    
    return hasPositive && hasNegative;
  }, [defaultOrganizations]);

  const getAllOrganizations = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/brand/v1/organizations/all`);
      const organizationsData = res.data.response.map((org: any) => ({
        value: org.id,
        label: org.name,
        id: org.id,
        name: org.name
      }));
      setAllOrganizations(organizationsData);
      setLoading(false);
    } catch (error) {
      console.log(error, 'error-getall-organizations');
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllOrganizations();
  }, []);

  // Update selectedOrganizations when defaultOrganizations or allOrganizations change
  useEffect(() => {
    if (!allOrganizations?.length || !defaultOrganizations?.length) {
      setSelectedOrganizations([]);
      return;
    }

    // Map organization IDs to their absolute values for matching
    const organizationIdToType: Record<string, 'include' | 'exclude'> = {}; // Store whether ID is positive or negative
    defaultOrganizations.forEach(id => {
      const normalizedId = normalizeOrganizationId(id);
      const absId = Math.abs(normalizedId);
      organizationIdToType[absId.toString()] = normalizedId > 0 ? 'include' : 'exclude';
    });
    
    // Get the absolute values of defaultOrganizations for matching with allOrganizations
    const absIds = defaultOrganizations.map(id => Math.abs(normalizeOrganizationId(id)));
    
    // Find the corresponding organization objects
    const matchedOrganizations = allOrganizations.filter(org => {
      const orgId = Math.abs(normalizeOrganizationId(org.id));
      return absIds.includes(orgId);
    });
    
    // Filter to show only organizations that match the current mode
    const relevantOrganizations = matchedOrganizations.filter(org => {
      const orgId = Math.abs(normalizeOrganizationId(org.id));
      const orgIdKey = orgId.toString();
      return (mode === 'exclude' && organizationIdToType[orgIdKey] === 'exclude') || 
             (mode === 'include' && organizationIdToType[orgIdKey] === 'include');
    });
    
    setSelectedOrganizations(relevantOrganizations);
  }, [defaultOrganizations, allOrganizations, mode]);

  const handleOrganizationChange = (selectedValues: string[]) => {
    // Convert string values back to organization objects
    const newValue = selectedValues.map((valueStr) => {
      const orgId = parseInt(valueStr, 10);
      return allOrganizations.find(org => Math.abs(normalizeOrganizationId(org.id)) === Math.abs(orgId));
    }).filter(Boolean) as any[];
    
    setSelectedOrganizations(newValue);
    
    // Create new IDs array with the appropriate sign based on current mode only
    const newModeIds = newValue.map((org: any) => {
      const orgId = Math.abs(normalizeOrganizationId(org.id));
      return mode === 'exclude' ? -orgId : orgId;
    });
    
    // Only set the current mode IDs, don't try to preserve opposite mode
    setDefaultOrganizations('organizations_exclude', newModeIds);
  };

  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMode = event.target.value as 'exclude' | 'include';
    
    // Clear the selection when changing modes
    setMode(newMode);
    setSelectedOrganizations([]);
    setDefaultOrganizations('organizations_exclude', []);
    
    // Re-select organizations based on new mode (will trigger useEffect)
  };
  
  // Filter the available organizations to exclude already selected organizations (regardless of +/- sign)
  const filteredOptions = useMemo(() => {
    if (!allOrganizations?.length) return [];
    if (!defaultOrganizations || !Array.isArray(defaultOrganizations) || defaultOrganizations.length === 0) return allOrganizations;
    
    // Create a set of absolute IDs that are already selected in either mode
    const selectedAbsIds = new Set(
      defaultOrganizations.map(id => Math.abs(normalizeOrganizationId(id)))
    );
    
    // Filter out organizations that are already selected (by absolute ID)
    return allOrganizations.filter(org => {
      const orgId = Math.abs(normalizeOrganizationId(org.id));
      return !selectedAbsIds.has(orgId);
    });
  }, [allOrganizations, defaultOrganizations]);
  
  // Convert selectedOrganizations to string array for MultiSelectTag
  const selectedValues = useMemo(() => {
    if (!selectedOrganizations || !Array.isArray(selectedOrganizations) || selectedOrganizations.length === 0) return [];
    return selectedOrganizations
      .filter(org => org && org.id !== undefined)
      .map(org => Math.abs(normalizeOrganizationId(org.id)).toString());
  }, [selectedOrganizations]);

  // Convert ALL organizations to MultiSelectTag format (needed to display selected tags)
  const multiSelectOptions = useMemo(() => {
    return allOrganizations.map(org => ({
      label: org.label || org.name || '',
      value: Math.abs(normalizeOrganizationId(org.id)).toString()
    }));
  }, [allOrganizations]);

  // Show appropriate label based on mode
  const autocompleteLabel = mode === 'exclude' 
    ? "Select organizations to exclude" 
    : "Select organizations to include";
    
  // Show appropriate placeholder text based on mode
  const autocompletePlaceholder = mode === 'exclude'
    ? selectedOrganizations.length === 0 ? "All organizations included" : ""
    : selectedOrganizations.length === 0 ? "No organizations included" : "";
  
  return (
    <div className="mt-4">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Label className="text-sm font-medium">Organization Access Mode</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 text-gray-500 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Exclude mode: Remove access for only specific organizations (negative IDs). Include mode: Grant access to only specific organizations (positive IDs).</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {hasMixedMode && (
          <p className="text-sm text-red-600 mb-2">
            Warning: Your current configuration has mixed positive and negative IDs. 
            This is not recommended. Please choose one mode.
          </p>
        )}
        
        <RadioGroup
          value={mode}
          onValueChange={(value) => handleModeChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>)}
          className="flex flex-row gap-6"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="exclude" id="org-exclude" />
            <Label htmlFor="org-exclude" className="flex items-center gap-2 cursor-pointer">
              Exclude specific organizations
              <Badge variant={mode === 'exclude' ? 'destructive' : 'secondary'} className="h-5 text-xs">
                Example
              </Badge>
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="include" id="org-include" />
            <Label htmlFor="org-include" className="flex items-center gap-2 cursor-pointer">
              Include only specific organizations
              <Badge variant={mode === 'include' ? 'default' : 'secondary'} className="h-5 text-xs bg-primary text-primary-foreground">
                Example
              </Badge>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <SpinnerLoader className="h-6 w-6" />
        </div>
      ) : (
        <MultiSelectCheckboxTag
          placeholder={autocompletePlaceholder || autocompleteLabel}
          value={selectedValues}
          onChange={handleOrganizationChange}
          options={multiSelectOptions}
          containerClassName="w-full"
          showSelectAll
        />
      )}
      
      <p className="text-sm text-gray-600 mt-2">
        {mode === 'exclude' 
          ? "If no organizations are selected, all organizations will have access." 
          : "If no organizations are selected, no organizations will have access."}
      </p>
    </div>
  );
};

export default OrganizationMultiSelect; 